import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { useJobMasterListScreen } from './hooks/useJobMasterListScreen'
import { appConfirm } from '../../../components/AppConfirmModal'

const LANE_GRADIENTS = [
  'from-violet-500/10 to-violet-500/5 border-violet-500/20',
  'from-sky-500/10 to-sky-500/5 border-sky-500/20',
  'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
  'from-amber-500/10 to-amber-500/5 border-amber-500/20',
  'from-rose-500/10 to-rose-500/5 border-rose-500/20',
  'from-teal-500/10 to-teal-500/5 border-teal-500/20',
  'from-indigo-500/10 to-indigo-500/5 border-indigo-500/20',
]

const LANE_ACCENT = [
  'text-violet-600 bg-violet-500/10',
  'text-sky-600 bg-sky-500/10',
  'text-emerald-600 bg-emerald-500/10',
  'text-amber-600 bg-amber-500/10',
  'text-rose-600 bg-rose-500/10',
  'text-teal-600 bg-teal-500/10',
  'text-indigo-600 bg-indigo-500/10',
]

function statusDot(status) {
  if (status === 'Active') return 'bg-emerald-500'
  if (status === 'Pending Review' || status === 'Pending') return 'bg-amber-500'
  return 'bg-outline'
}

export default function JobMasterListScreen() {
  const { state, actions } = useJobMasterListScreen()

  const groupedJobs = useMemo(() => {
    const groups = {}
    state.rows.forEach((row) => {
      const parentId = row.parentTaskMasterId || 'unassigned'
      const parentName = row.parentTaskMasterName || 'Unassigned'
      if (!groups[parentId]) {
        groups[parentId] = { id: parentId, name: parentName, items: [] }
      }
      groups[parentId].items.push(row)
    })
    Object.values(groups).forEach((g) =>
      g.items.sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    )
    return Object.values(groups)
  }, [state.rows])

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-8">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Master Configuration</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-on-surface">Job Master</h1>
          <p className="mt-1 text-sm text-on-surface-variant">Sub-task blueprints — configure checklist sequences, effort weights, and SLA overrides.</p>
        </div>
        <Link
          to="/masters/job-master/entry"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-on-primary shadow-md shadow-primary/25 transition hover:brightness-110"
        >
          <span className="material-symbols-outlined text-base">add</span>
          New Job Type
        </Link>
      </header>

      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 rounded-2xl bg-surface-container-low px-5 py-3.5">
        {/* Search */}
        <div className="flex flex-1 items-center gap-3">
          <span className="material-symbols-outlined text-lg text-on-surface-variant">search</span>
          <input
            type="text"
            value={state.searchText}
            onChange={(e) => actions.setSearchText(e.target.value)}
            placeholder="Search job types..."
            className="flex-1 border-none bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
          />
        </div>

        {/* Parent filter */}
        <div className="sm:w-56">
          <AppDropdown
            value={state.taskMasterId}
            onChange={actions.setTaskMasterId}
            placeholder="All parent blueprints"
            triggerClassName="w-full rounded-xl border-0 bg-surface-container-high px-3 py-2 text-sm text-on-surface"
            options={[
              { value: '', label: 'All parent blueprints' },
              ...state.taskMasterOptions.map((o) => ({
                value: String(o.id),
                label: `${o.task_type_name} (${o.task_type_code})`,
              })),
            ]}
          />
        </div>

        {/* Status toggle */}
        <div className="flex rounded-xl bg-surface-container-high p-1 gap-1">
          {[
            { id: 'active', label: 'Active' },
            { id: 'pending', label: 'Pending Review' },
            { id: 'inactive', label: 'Inactive' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => actions.setStatusFilter(tab.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                state.statusFilter === tab.id
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Count pill */}
        <div className="flex items-center gap-1.5 rounded-xl bg-primary/10 px-3 py-1.5">
          <span className="material-symbols-outlined text-sm text-primary">inventory_2</span>
          <span className="text-sm font-extrabold text-primary">{state.totalCount || state.rows.length}</span>
        </div>
      </div>

      {/* ── Alerts ─────────────────────────────────────────────────────── */}
      {state.error ? (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}
      {state.success ? (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.success}</div>
      ) : null}

      {/* ── Lanes ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-5">
        {state.isLoading ? (
          <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
            Loading job templates...
          </div>
        ) : state.rows.length === 0 ? (
          <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
            No job templates found.
          </div>
        ) : (
          groupedJobs.map((group, groupIdx) => {
            const gradient = LANE_GRADIENTS[groupIdx % LANE_GRADIENTS.length]
            const accent = LANE_ACCENT[groupIdx % LANE_ACCENT.length]

            return (
              <div
                key={group.id}
                className={`rounded-2xl border bg-gradient-to-r ${gradient} overflow-hidden`}
              >
                {/* Lane Header */}
                <div className="flex items-center justify-between gap-4 px-5 py-3.5">
                  <div>
                    <h3 className={`text-base font-extrabold tracking-tight ${accent.split(' ')[0]}`}>{group.name}</h3>
                    <p className="mt-0.5 text-[11px] font-semibold text-on-surface-variant">
                      {group.items.length} {group.items.length === 1 ? 'step' : 'steps'} in sequence
                    </p>
                  </div>

                  {group.id !== 'unassigned' && (
                    <Link
                      to={`/masters/task-master/entry?id=${group.id}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-surface-container-lowest/70 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-primary transition hover:bg-surface-container-lowest"
                    >
                      <span className="material-symbols-outlined text-xs">open_in_new</span>
                      View Blueprint
                    </Link>
                  )}
                </div>

                {/* Scrollable chips row */}
                <div className="flex gap-3 overflow-x-auto px-5 pb-5 pt-1 scrollbar-thin scrollbar-thumb-outline-variant/30 scrollbar-track-transparent">
                  {group.items.map((row, idx) => (
                    <div
                      key={row.id}
                      className="group relative flex-none w-52 rounded-xl border border-outline-variant/15 bg-surface-container-lowest p-4 shadow-sm transition duration-150 hover:shadow-md hover:-translate-y-0.5"
                    >
                      {/* Sequence badge */}
                      <div className="mb-3 flex items-center justify-between">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-[11px] font-black text-on-primary shadow-sm">
                          #{row.sequenceOrder ?? idx + 1}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {row.status === 'Pending Review' && (
                            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">Pending</span>
                          )}
                          <span className={`h-2 w-2 rounded-full ${statusDot(row.status)}`} title={row.status} />
                        </div>
                      </div>

                      {/* Name & Code */}
                      <p className="text-sm font-extrabold text-on-surface leading-snug line-clamp-2">{row.name}</p>
                      <p className="mt-0.5 text-[9px] font-mono uppercase tracking-wider text-on-surface-variant">{row.code}</p>

                      {/* Stats */}
                      <div className="mt-3 flex items-center gap-2 text-[10px]">
                        <span className="flex items-center gap-1 text-on-surface-variant">
                          <span className="material-symbols-outlined text-xs">schedule</span>
                          {row.defaultEstimatedHours?.toFixed(1) || '0'}h
                        </span>
                        {row.slaHours ? (
                          <span className="flex items-center gap-1 rounded-md bg-amber-500/10 px-1.5 py-0.5 font-semibold text-amber-700">
                            <span className="material-symbols-outlined text-xs">hourglass_empty</span>
                            {row.slaHours}h SLA
                          </span>
                        ) : (
                          <span className="text-on-surface-variant/50">SLA: inherited</span>
                        )}
                      </div>

                      {/* Hover actions */}
                      <div className="mt-3 flex items-center justify-end gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                        <Link
                          to={`/masters/job-master/entry?id=${row.id}`}
                          className="rounded-lg p-1.5 text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition"
                          title="Edit"
                        >
                          <span className="material-symbols-outlined text-sm">edit</span>
                        </Link>
                        <button
                          type="button"
                          disabled={state.isMutating}
                          onClick={async () => {
                            const confirmed = await appConfirm({
                              title: row.isArchived ? 'Delete Job Blueprint' : 'Archive Job Blueprint',
                              message: row.isArchived
                                ? `Are you sure you want to permanently delete "${row.title}"?`
                                : `Archive "${row.title}"?`,
                              confirmText: row.isArchived ? 'Delete' : 'Archive',
                              variant: 'danger',
                            })
                            if (confirmed) actions.handleRowDeleteAction(row)
                          }}
                          className="rounded-lg p-1.5 text-on-surface-variant hover:bg-error-container hover:text-error transition disabled:opacity-50"
                          title={row.isArchived ? 'Delete' : 'Archive'}
                        >
                          <span className="material-symbols-outlined text-sm">delete</span>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add to this sequence shortcut */}
                  <Link
                    to={`/masters/job-master/entry${group.id !== 'unassigned' ? `?taskMasterId=${group.id}` : ''}`}
                    className="flex-none w-36 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-outline-variant/30 bg-surface-container-lowest/40 p-4 text-center text-on-surface-variant transition hover:border-primary/40 hover:text-primary hover:bg-primary/5"
                  >
                    <span className="material-symbols-outlined text-xl">add_circle</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider leading-tight">Add step</span>
                  </Link>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-6 py-4 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <span className="text-sm text-on-surface-variant">
          Showing <strong>{state.rangeStart}–{state.rangeEnd}</strong> of <strong>{state.totalCount || state.rows.length}</strong> templates
        </span>
        <AppPagination
          page={state.page}
          totalPages={state.totalPages}
          onPageChange={actions.goToPage}
          onPrevious={actions.goToPreviousPage}
          onNext={actions.goToNextPage}
          canPrevious={state.page > 1}
          canNext={Boolean(state.hasMore) || state.page < state.totalPages}
        />
      </div>
    </div>
  )
}
